import { debugFlag } from '@/app/debugFlags/debugFlags';
import { authClient } from '@/auth/auth';
import { apiClient } from '@/shared/api/apiClient';
import { requestRefreshIfClientStale } from '@/shared/utils/requestRefreshIfClientStale';
import { captureException } from '@sentry/react';
import type z from 'zod';

export class ApiError extends Error {
  status: number;
  details?: string;
  method?: string;

  constructor(message: string, status: number, method?: string, details?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status; // Fetch response status code
    this.method = method; // Fetch request method
    this.details = details; // Details useful for debugging
  }
}

/**
 * Error thrown when the schema configuration is missing or invalid.
 * This indicates a programming error (e.g., missing schema in ApiSchemas),
 * not a runtime API failure.
 */
export class SchemaConfigurationError extends TypeError {
  path: string;
  method?: string;

  constructor(message: string, path: string, method?: string) {
    super(message);
    this.name = 'SchemaConfigurationError';
    this.path = path;
    this.method = method;
  }
}

type FetchFromApiInit = RequestInit & {
  skipAuthRedirect?: boolean;
};

// `JSON.parse` wordings for "the body ended before a value could be parsed",
// i.e. an empty or truncated body, across engines. Chrome and Safari can also
// surface a body cut short mid-stream as a `TypeError`, which the re-throw below
// already handles; these are the cases where enough arrived for the parse to
// start but not finish.
//
// The `Unterminated …` entries cover V8's per-token wordings: since Chrome 114
// a body cut off *inside* a token reports "Unterminated string in JSON at
// position N" rather than the older catch-all "Unexpected end of JSON input",
// so on current Chrome — which is most of the fleet — a truncated body was
// still falling through to the staleness check and the capture below
// (INTERNAL-6Q5, a 35 MB /google/sheets/download response). Every engine uses
// "unterminated" only for input that ran out mid-token, so these can't collide
// with a body that is genuinely malformed rather than cut short.
//
// The bare `end of data` entry is the same completeness fix for SpiderMonkey:
// Firefox only says "unexpected end of data" when the body ran out where a
// *value* was expected, and uses a distinct wording per structural position
// otherwise — "end of data when ',' or ']' was expected", "end of data when
// property name was expected", "end of data after property value in object",
// "end of data while reading object contents". So a truncated array or object
// (INTERNAL-6DV, a `[]`-bodied /agent-connections response on Firefox 153 whose
// 2-byte body arrived cut short) was still falling through to the capture below.
// Every one of these begins with "end of data", and SpiderMonkey uses that
// phrase only for input that ran out, so the shared prefix is safe.
//
// Compared case-insensitively because the same condition is capitalized
// differently per engine ("Unterminated string in JSON" in V8, "unterminated
// string literal" in Firefox).
const EMPTY_OR_TRUNCATED_JSON_PATTERNS = [
  'unexpected end of json input', // Chrome/Edge
  'end of data', // Firefox — covers "unexpected end of data" and its structural variants
  'unexpected eof', // Safari
  'unexpected end of script', // Safari (older)
  'unterminated string', // Chrome >= 114 / Firefox / Safari — cut inside a string
  'unterminated fractional number', // Chrome >= 114 — cut inside a number
];

export async function fetchFromApi<T>(
  path: string,
  init: FetchFromApiInit,
  schema: z.Schema<T>
): Promise<z.infer<typeof schema>> {
  const { skipAuthRedirect = false, ...requestInit } = init;
  // Validate that schema is defined and has safeParse method before proceeding
  if (!schema || typeof schema.safeParse !== 'function') {
    const schemaType = schema === undefined ? 'undefined' : schema === null ? 'null' : typeof schema;
    const errorMessage =
      `Schema configuration error for API call: ${requestInit.method} ${path}. ` +
      `Expected a Zod schema but received ${schemaType}. ` +
      `This likely indicates a missing or incorrect schema definition in ApiSchemas.`;
    console.error(errorMessage, { path, method: requestInit.method, schemaType });
    const error = new SchemaConfigurationError(errorMessage, path, requestInit.method);
    captureException(error);
    throw error;
  }

  const debugTime = `API ${requestInit.method} ${path}`;
  if (debugFlag('debugShowAPITimes')) console.time(debugTime);

  // We'll automatically inject additional headers to the request, starting with auth
  const isAuthenticated = await authClient.isAuthenticated();
  const skipRedirect = skipAuthRedirect || window.location.pathname.includes('/login-result');
  const token = isAuthenticated ? await authClient.getTokenOrRedirect(skipRedirect) : '';
  const headers = new Headers(requestInit.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // And if we're submitting `FormData`, let the browser set the content-type automatically
  // This allows files to upload properly. Otherwise, we assume it's JSON.
  if (!(requestInit.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  // And finally, we'll set the headers back on the request
  requestInit.headers = headers;

  // Make API call
  const url = apiClient.getApiUrl() + path;
  const response = await fetch(url, requestInit);

  // Handle if the response is not JSON
  const json = await response.json().catch(async (error: unknown) => {
    // Only a parse failure means the body wasn't JSON. `response.json()` also
    // rejects when reading the body fails *after* the headers arrived: the
    // request was aborted mid-stream (`AbortError`) or the connection dropped
    // (`TypeError`). Those say nothing about the response's content, so re-throw
    // them untouched — reporting them as "Non-JSON API response" misattributes a
    // transport failure to a server/gateway bug, and the synthetic Error below
    // also hides it from `isNetworkError`/`isNetworkFailure`, which classify off
    // the original exception.
    //
    // INTERNAL-6N8 was this: `useCellEditHistory` aborts the superseded
    // /cell-history request on the next cursor move, which rejected a body read
    // already in progress on a 200 whose body was 14 bytes of valid JSON.
    // Re-throwing also makes both abort timings behave alike — an abort before
    // the headers rejects `fetch()` itself, which already propagates raw.
    if (!(error instanceof SyntaxError)) {
      throw error;
    }

    // An empty or truncated body is a transport symptom too, not a content one:
    // the connection dropped part-way through a body we had already started
    // parsing. Nothing about what the server *sent* is wrong, so neither the
    // staleness check nor a capture applies — INTERNAL-6C1 was this, a 54KB
    // /v0/agent-catalog body (the largest of three refreshes fired within 600ms)
    // read while the connection was failing; the same breadcrumb window shows a
    // /v0/ai/chat POST failing outright with no status at all. That endpoint
    // passes no `AbortSignal`, so it can't be the abort case above. Still throw
    // the contextful `ApiError` so callers behave exactly as before.
    const parseMessage = error.message.toLowerCase();
    if (EMPTY_OR_TRUNCATED_JSON_PATTERNS.some((pattern) => parseMessage.includes(pattern))) {
      throw new ApiError('Response is not JSON', response.status, requestInit.method, url);
    }

    // A non-JSON body where JSON is expected almost always means a stale client
    // received an HTML page (the SPA shell or a gateway/error page) for an
    // endpoint whose contract has since changed — e.g. "Unexpected token '<',
    // "<!DOCTYPE "... is not valid JSON". Prompt a refresh and throw a
    // contextful ApiError (with the URL) instead of capturing a location-less
    // SyntaxError, matching the schema-mismatch branch below.
    const staleness = await requestRefreshIfClientStale();
    // If the client is confirmed current, this isn't deploy skew — it's a
    // genuine server/gateway bug (e.g. HTML returned for a 200), so preserve
    // observability with a contextful capture.
    //
    // Only for a *successful* status, though. quadratic-api answers every error
    // with a JSON body — its error middleware (quadratic-api/src/app.ts) and
    // every `res.status(4xx).json(...)` call site — so a non-JSON body on an
    // error status never came from the API at all: it's an intermediary's error
    // page (CDN/WAF/load-balancer HTML). Nothing in that is fixable from client
    // code, and the actionable part, the status, already rides on the ApiError
    // below (plus httpClientIntegration reports 5xx on its own).
    //
    // INTERNAL-6C5 was exactly this: a 403 with a ~15KB HTML body for a
    // /files/:uuid/thumbnail POST, from a user who demonstrably had edit access
    // (their /files/:uuid PATCH one second earlier returned 200) — i.e. the edge
    // rejected the multipart upload before it ever reached our handler, whose
    // own 403 is a 46-byte JSON object.
    //
    // This also aligns the two API helpers: `xhrFromApi` returns on
    // `xhr.status >= 400` before it parses, so its copy of this capture has
    // always been success-only.
    //
    // The staleness check above still runs either way: a stale client hitting a
    // route this deploy removed gets express's HTML 404, and prompting the
    // refresh is the recovery for it.
    if (staleness === 'current' && response.ok) {
      captureException(new Error(`Non-JSON API response from ${url}`), {
        // The synthesized Error carries no trace of what actually failed to
        // parse, so pass the parse error through — that text ("Unexpected token
        // '<'", …) is what identifies the body.
        extra: { status: response.status, method: requestInit.method, parseError: String(error) },
      });
    }
    throw new ApiError('Response is not JSON', response.status, requestInit.method, url);
  });

  // Handle response if a server error is returned
  if (!response.ok) {
    // TODO: ensure API only ever returns uniform error response, e.g. `json.errors` or `json.error.message`
    let details = 'No detailed error message provided';
    if (json?.error?.message) {
      details = json.error.message;
    } else if (json?.errors) {
      details = JSON.stringify(json.errors);
    }
    throw new ApiError(`Failed to fetch ${url}`, response.status, requestInit.method, details);
  }

  // Compare the response to the expected schema
  const result = schema.safeParse(json);

  if (!result.success) {
    console.error(`Zod schema validation failed at: ${path}`, JSON.stringify(result.error, null, 2));

    // A successful response that fails our schema usually means this client
    // bundle is stale relative to the deployed API; prompt a (required) refresh.
    void requestRefreshIfClientStale();

    const details = JSON.stringify(result.error);
    throw new ApiError('Unexpected response schema', response.status, requestInit.method, details);
  }

  if (debugFlag('debugShowAPITimes')) console.timeEnd(debugTime);
  return result.data;
}
